// client/src/components/AddPartsOrderModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const SHIPPING_METHODS = ['Postal Service', 'LTL', 'Piggyback', 'Pickup'];

function AddPartsOrderModal({ show, handleClose, refreshOrders, setSubmissionMessage }) {
  const [formData, setFormData] = useState({
    orderType: 'Parts',
    dealerId: '',
    dealerName: '',
    customerName: '',
    customWork: '',
    shippingMethod: '',
    revenue: '',
    orderDate: '',
    invoiceNumber: '',
    tbcNumber: ''
  });

  const [piggybackOption, setPiggybackOption] = useState('next');
  const [piggybackOrderName, setPiggybackOrderName] = useState('');
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [modalMessage, setModalMessage] = useState('');
  const [modalMessageType, setModalMessageType] = useState('danger');
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(true);
  const [dealersError, setDealersError] = useState('');

  const onHide = () => {
    setFormData({
      orderType: 'Parts',
      dealerId: '',
      dealerName: '',
      customerName: '',
      customWork: '',
      shippingMethod: '',
      revenue: '',
      orderDate: '',
      invoiceNumber: '',
      tbcNumber: ''
    });
    setPiggybackOption('next');
    setPiggybackOrderName('');
    setModalMessage('');
    setModalMessageType('danger');
    setDealersError('');
    setTouched({});
    setErrors({});
    handleClose();
  };

  useEffect(() => {
    const fetchDealers = async () => {
      setLoadingDealers(true);
      setDealersError('');
      try {
        const response = await fetch(`${API_ROOT_URL}/dealers`, {
          headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        data.sort((a, b) => a.dealerName.localeCompare(b.dealerName));
        setDealers(data);
      } catch (error) {
        console.error('Error fetching dealers:', error);
        setDealersError('Failed to load dealers. Please check your network and backend server.');
      } finally {
        setLoadingDealers(false);
      }
    };

    if (show) {
      setModalMessage('');
      setSubmissionMessage('', '');
      fetchDealers();
    }
  }, [show, setSubmissionMessage]);

  const buildErrors = (data, pigOpt, pigName) => {
    const e = {};
    if (!data.dealerId) e.dealerId = 'Please select a dealer.';
    if (!data.dealerName || !data.dealerName.trim()) e.dealerName = 'Dealer Name is required.';
    if (!data.customerName || !data.customerName.trim()) e.customerName = 'Customer Name is required.';
    if (!data.shippingMethod) e.shippingMethod = 'Shipping Method is required.';

    if (data.invoiceNumber && !data.invoiceNumber.trim()) {
      e.invoiceNumber = 'Invoice number cannot be just whitespace.';
    }

    if (data.tbcNumber && !data.tbcNumber.trim()) {
      e.tbcNumber = 'TBC number cannot be just whitespace.';
    }

    if (data.shippingMethod === 'Piggyback' && pigOpt === 'custom') {
      if (!pigName || !pigName.trim()) {
        e.piggybackOrderName = 'Enter the specific order name for piggyback.';
      } else if (pigName.trim().length < 2) {
        e.piggybackOrderName = 'Piggyback order name must be at least 2 characters.';
      }
    }

    if (data.revenue !== '' && (Number.isNaN(Number(data.revenue)) || Number(data.revenue) < 0)) {
      e.revenue = 'Revenue must be a non-negative amount.';
    }

    if (data.orderDate) {
      const d = new Date(data.orderDate);
      if (Number.isNaN(d.getTime())) e.orderDate = 'Invalid date.';
    }

    return e;
  };

  const validateAndSet = (next = formData, nextPigOpt = piggybackOption, nextPigName = piggybackOrderName) => {
    const e = buildErrors(next, nextPigOpt, nextPigName);
    setErrors(e);
    return e;
  };

  const setFieldTouched = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldTouched(name);

    setFormData(prev => {
      if (name === 'dealerId') {
        const selected = dealers.find(d => d.dealerId === value);
        const next = { ...prev, dealerId: value, dealerName: selected ? selected.dealerName : '' };
        setTouched(t => ({ ...t, dealerName: true }));
        validateAndSet(next, piggybackOption, piggybackOrderName);
        return next;
      }

      const next = { ...prev, [name]: value };
      if (name === 'shippingMethod' && value !== 'Piggyback') {
        setPiggybackOption('next');
        setPiggybackOrderName('');
        validateAndSet(next, 'next', '');
        return next;
      }

      validateAndSet(next, piggybackOption, piggybackOrderName);
      return next;
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFieldTouched(name);
    validateAndSet();
  };

  const buildFinalShippingMethod = () => {
    if (formData.shippingMethod !== 'Piggyback') return formData.shippingMethod || '';
    if (piggybackOption === 'next') return 'Piggyback with Next Cover';
    const trimmed = piggybackOrderName.trim();
    if (!trimmed) return '';
    return `Piggyback with ${trimmed}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setModalMessage('');

    const touch = {
      dealerId: true,
      dealerName: true,
      customerName: true,
      shippingMethod: true
    };
    if (formData.shippingMethod === 'Piggyback' && piggybackOption === 'custom') {
      touch.piggybackOrderName = true;
    }

    if (formData.invoiceNumber) touch.invoiceNumber = true;
    if (formData.tbcNumber) touch.tbcNumber = true;
    if (formData.revenue !== '') touch.revenue = true;
    if (formData.orderDate) touch.orderDate = true;

    setTouched(prev => ({ ...prev, ...touch }));

    const eObj = validateAndSet(formData, piggybackOption, piggybackOrderName);
    if (Object.keys(eObj).length > 0) {
      setModalMessage('Please correct the highlighted fields.');
      setModalMessageType('danger');
      setLoading(false);
      return;
    }

    try {
      const orderData = {
        orderType: 'Parts',
        dealerId: formData.dealerId,
        dealerName: formData.dealerName,
        customerName: formData.customerName,
        customWork: formData.customWork || '',
        shippingMethod: buildFinalShippingMethod(),
        revenue: formData.revenue === '' ? undefined : Number(formData.revenue),
        orderDate: formData.orderDate ? new Date(formData.orderDate) : undefined,
        invoiceNumber: formData.invoiceNumber || undefined,
        tbcNumber: formData.tbcNumber || undefined
      };

      const response = await fetch(`${API_ROOT_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmissionMessage(result.message || 'Parts Order created successfully!', 'success');
        refreshOrders();
        onHide();
      } else {
        setModalMessage(result.message || 'Failed to create Parts Order.');
        setModalMessageType('danger');
      }
    } catch (error) {
      console.error('Error creating parts order:', error);
      setModalMessage('Network error or server is unreachable. Please try again.');
      setModalMessageType('danger');
    } finally {
      setLoading(false);
    }
  };

  const invalid = (name) => !!(touched[name] && errors[name]);
  const feedback = (name) => (touched[name] && errors[name]) ? errors[name] : '';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Parts Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {dealersError && <Alert variant="danger">{dealersError}</Alert>}
        {modalMessage && <Alert variant={modalMessageType}>{modalMessage}</Alert>}

        {loadingDealers ? (
          <div className="text-center my-3">
            <Spinner animation="border" size="sm" /> Loading Dealers...
          </div>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {/* Order Type (read-only, gray) */}
            <Form.Group className="mb-3">
              <Form.Label>Order Type</Form.Label>
              <Form.Control type="text" value="Parts" readOnly style={{ backgroundColor: '#e9ecef' }} />
            </Form.Group>

            {/* Invoice Number */}
            <Form.Group className="mb-3">
              <Form.Label>Invoice Number (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={invalid('invoiceNumber')}
                placeholder="Enter invoice number"
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {feedback('invoiceNumber')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* TBC Number */}
            <Form.Group className="mb-3">
              <Form.Label>TBC Number (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="tbcNumber"
                value={formData.tbcNumber}
                onChange={(e) => handleChange({ target: { name: 'tbcNumber', value: e.target.value.toUpperCase() } })}
                onBlur={handleBlur}
                isInvalid={invalid('tbcNumber')}
                placeholder="Enter TBC number"
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {feedback('tbcNumber')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Dealer */}
            <Form.Group className="mb-3">
              <Form.Label>Dealer</Form.Label>
              <Form.Select
                name="dealerId"
                value={formData.dealerId}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={invalid('dealerId')}
                disabled={loading || dealers.length === 0}
              >
                <option value="">{dealers.length > 0 ? "Select a Dealer" : "No Dealers Available"}</option>
                {dealers.map(dealer => (
                  <option key={dealer.dealerId} value={dealer.dealerId}>
                    {dealer.dealerName} ({dealer.dealerId})
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {feedback('dealerId')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Customer Name */}
            <Form.Group className="mb-3">
              <Form.Label>Customer Name</Form.Label>
              <Form.Control
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={invalid('customerName')}
                disabled={loading}
                placeholder="Enter customer's name"
              />
              <Form.Control.Feedback type="invalid">
                {feedback('customerName')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Custom Work */}
            <Form.Group className="mb-3">
              <Form.Label>Custom Work</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="customWork"
                value={formData.customWork}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Describe the parts or details"
                disabled={loading}
              />
            </Form.Group>

            {/* Shipping Method */}
            <Form.Group className="mb-3">
              <Form.Label>Shipping Method</Form.Label>
              <Form.Select
                name="shippingMethod"
                value={formData.shippingMethod}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={invalid('shippingMethod')}
                disabled={loading}
              >
                <option value="" disabled>Select Shipping Method</option>
                {SHIPPING_METHODS.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {feedback('shippingMethod')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Piggyback Options */}
            {formData.shippingMethod === 'Piggyback' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="radio"
                    label="Piggyback with Next Cover"
                    checked={piggybackOption === 'next'}
                    onChange={() => {
                      setPiggybackOption('next');
                      validateAndSet(formData, 'next', piggybackOrderName);
                    }}
                    disabled={loading}
                  />
                  <Form.Check
                    type="radio"
                    label="Piggyback with Specific Order"
                    checked={piggybackOption === 'custom'}
                    onChange={() => {
                      setPiggybackOption('custom');
                      validateAndSet(formData, 'custom', piggybackOrderName);
                    }}
                    disabled={loading}
                  />
                </Form.Group>
                {piggybackOption === 'custom' && (
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Enter Order Name"
                      value={piggybackOrderName}
                      onChange={(e) => {
                        setPiggybackOrderName(e.target.value);
                        setFieldTouched('piggybackOrderName');
                        validateAndSet(formData, piggybackOption, e.target.value);
                      }}
                      onBlur={() => {
                        setFieldTouched('piggybackOrderName');
                        validateAndSet();
                      }}
                      isInvalid={invalid('piggybackOrderName')}
                      disabled={loading}
                    />
                    <Form.Control.Feedback type="invalid">
                      {feedback('piggybackOrderName')}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              </>
            )}

            {/* Revenue (optional) */}
            <Form.Group className="mb-3">
              <Form.Label>Revenue (Optional)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="revenue"
                value={formData.revenue}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={invalid('revenue')}
                min="0"
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {feedback('revenue')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Order Date (Optional) */}
            <Form.Group className="mb-4">
              <Form.Label>Order Date</Form.Label>
              <Form.Control
                type="date"
                name="orderDate"
                value={formData.orderDate}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-auto"
                isInvalid={invalid('orderDate')}
              />
              <Form.Control.Feedback type="invalid">
                {feedback('orderDate')}
              </Form.Control.Feedback>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={loading} className="w-100">
              {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Create Parts Order'}
            </Button>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddPartsOrderModal;
